import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from "@/lib/api";
import { isSearchInFlight } from "@/types/api";
import type {
  Alert,
  AlertsResponse,
  AlertStatus,
  DashboardSummary,
  DashboardTrends,
  Integration,
  IntegrationsResponse,
  IntegrationTestResult,
  CreateIntegrationRequest,
  CreateIntegrationResponse,
  MspTenantsResponse,
  MspDashboardSummaryResponse,
  MspBillingResponse,
  SyslogSourcesResponse,
  SyslogSourceCreated,
  CreateSyslogSourceRequest,
  MspDashboardHealthResponse,
  MspCreateTenantRequest,
  MspCreateTenantResponse,
  MspBulkCreateClient,
  MspBulkCreateResponse,
  MspOnboardingSummaryResponse,
  NotificationConfig,
  NotificationConfigsResponse,
  CreateNotificationConfigRequest,
  UpdateNotificationConfigRequest,
  NotificationPolicy,
  NotificationPoliciesResponse,
  UpsertNotificationPolicyRequest,
  TestNotificationResponse,
  BrandingConfig,
  BrandingResponse,
  LogoUploadResponse,
  BillingSubscriptionResponse,
  BillingSubscription,
  BillingUsageResponse,
  BillingUsageRealtimeResponse,
  BillingPortalResponse,
  BillingStatusResponse,
  BillingSubscribeResponse,
  BillingProfile,
  BillingProfileResponse,
  BillingInvoicesResponse,
  BillingInvoiceDownloadResponse,
  TenantUser,
  UsersResponse,
  CreateUserRequest,
  UpdateUserRequest,
  SearchRequest,
  SearchStartResponse,
  SearchResult,
  NaturalLanguageSearchRequest,
  NaturalLanguageTranslation,
  EntityTimelineResult,
  EntityTimelineStartResponse,
  SavedSearch,
  SavedSearchesResponse,
  OnboardingChecklist,
  AgentKeysResponse,
  CreateAgentKeyRequest,
  CreateAgentKeyResponse,
  FleetKeysResponse,
  CreateFleetKeyRequest,
  FleetKeyMintResponse,
  DeployAgentsResponse,
  ComplianceReportsResponse,
  ComplianceReportResponse,
  CreateComplianceReportRequest,
  Vulnerability,
  VulnerabilitiesResponse,
  VulnerabilitySummaryResponse,
  ThreatIntelSummary,
  ThreatIntelSearchResponse,
  ThreatIntelFeedsResponse,
  ReportSchedulesResponse,
  ReportSchedule,
  CreateReportScheduleRequest,
  UpdateReportScheduleRequest,
  ReportHistoryResponse,
  CspmFinding,
  CspmFindingsResponse,
  CspmPostureSummary,
  CspmChecksResponse,
  CspmScanResult,
  ExecutiveSummaryResponse,
  ExecutiveMetricsResponse,
  ExecutiveExportResponse,
  BillingState,
  HostsResponse,
  Workspace,
} from "@/types/api";

// ── Dashboard ──────────────────────────────────────────────

export function useDashboardSummary() {
  return useQuery<DashboardSummary>({
    queryKey: ["dashboard", "summary"],
    queryFn: () => apiGet<DashboardSummary>("/api/v1/dashboard/summary"),
    refetchInterval: 30_000,
  });
}

export function useHasIngestedData(): {
  hasData: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useDashboardSummary();
  // When isError=true and no stale data, callers treat summaryError as "unknown" (not "no data")
  // so they fall back to the "run a search" prompt rather than the "no data" empty state.
  // When isError=true and stale data exists, hasData reflects the stale counts — still correct
  // for the "unknown" fallback because callers also gate on !summaryError.
  if (isLoading || !data) return { hasData: false, isLoading, isError };
  const totalOpen =
    data.open_alerts.critical +
    data.open_alerts.high +
    data.open_alerts.medium +
    data.open_alerts.low;
  return {
    hasData: totalOpen > 0 || data.recent_alerts_24h > 0 || data.open_incidents > 0,
    isLoading: false,
    isError,
  };
}

export function useDashboardTrends(days: number = 7) {
  return useQuery<DashboardTrends>({
    queryKey: ["dashboard", "trends", days],
    queryFn: () =>
      apiGet<DashboardTrends>("/api/v1/dashboard/trends", {
        days: String(days),
      }),
    refetchInterval: 60_000,
  });
}

// ── Alerts ─────────────────────────────────────────────────

export function useAlerts(params?: {
  status?: string;
  severity?: string;
  owner?: "me" | "unassigned";
  search?: string;
  sort?: "newest" | "priority";
  limit?: number;
  offset?: number;
}) {
  return useQuery<AlertsResponse>({
    queryKey: ["alerts", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.status) qs.status = params.status;
      if (params?.severity) qs.severity = params.severity;
      if (params?.owner) qs.owner = params.owner;
      if (params?.search) qs.q = params.search;
      if (params?.sort) qs.sort = params.sort;
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<AlertsResponse>("/api/v1/alerts", qs);
    },
    refetchInterval: 30_000,
  });
}

export function useAssignAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, userId }: { alertId: string; userId: string | null }) =>
      apiPatch<{ id: string; assigned_to: string | null }>(
        `/api/v1/alerts/${alertId}/assign`,
        { user_id: userId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alert"] });
    },
  });
}

export function useAddAlertNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, text }: { alertId: string; text: string }) =>
      apiPost<{ status: string }>(`/api/v1/alerts/${alertId}/notes`, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alert"] });
    },
  });
}

export function useAlert(alertId: string | null) {
  return useQuery<{ alert: Alert }>({
    queryKey: ["alert", alertId],
    queryFn: () => apiGet<{ alert: Alert }>(`/api/v1/alerts/${alertId}`),
    enabled: !!alertId,
    // Poll while AI enrichment is still in flight so the detail panel populates
    // without a manual reopen. `null` = enrichment not yet recorded, "pending" =
    // running; both keep polling. "complete"/"failed" are terminal — stop. When
    // the panel closes (alertId → null) the query is disabled, which also halts
    // polling, so unmount/close needs no extra handling here.
    refetchInterval: (query) => {
      const alert = query.state.data?.alert;
      // No successful payload yet (initial load in flight, or the detail fetch
      // errored): don't poll. Polling is gated on a known null/pending
      // enrichment status, not on the absence of data — otherwise a persistent
      // 404/403/network failure would refetch every 5s indefinitely.
      if (!alert) return false;
      const status = alert.enrichment_status;
      if (status == null || status === "pending") return 5_000;
      return false;
    },
  });
}

export function useUpdateAlertStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, status }: { alertId: string; status: AlertStatus }) =>
      apiPatch<{ alert: Alert }>(`/api/v1/alerts/${alertId}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alert"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useBulkUpdateAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertIds, status }: { alertIds: string[]; status: AlertStatus }) =>
      apiPost<{ updated: number }>("/api/v1/alerts/bulk", {
        alert_ids: alertIds,
        status,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useAlertFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      alertId,
      feedback,
      note,
    }: {
      alertId: string;
      feedback: "true_positive" | "false_positive";
      note?: string;
    }) =>
      apiPost<{ alert: Alert }>(`/api/v1/alerts/${alertId}/feedback`, {
        feedback,
        note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alert"] });
    },
  });
}

export function useRetryEnrichment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId }: { alertId: string }) =>
      apiPost<{ alert: Alert }>(`/api/v1/alerts/${alertId}/retry-enrichment`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alert"] });
    },
  });
}

// ── Integrations ───────────────────────────────────────────

export function useIntegrations() {
  return useQuery<IntegrationsResponse>({
    queryKey: ["integrations"],
    queryFn: () => apiGet<IntegrationsResponse>("/api/v1/integrations"),
  });
}

// Polls GET /api/v1/integrations every 10 s while a CloudTrail integration is
// in the "waiting for first event" phase. The CloudTrailStep component uses this
// to detect the health_status flip from "unknown" → "healthy".
export function useCloudTrailPollFreshness(
  integrationId: string | null,
  enabled: boolean,
) {
  return useQuery<IntegrationsResponse>({
    queryKey: ["integrations", "cloudtrail-poll", integrationId],
    queryFn: () => apiGet<IntegrationsResponse>("/api/v1/integrations"),
    enabled: enabled && integrationId !== null,
    refetchInterval: enabled ? 10_000 : false,
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIntegrationRequest) =>
      apiPost<CreateIntegrationResponse>("/api/v1/integrations", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useTestIntegration() {
  return useMutation({
    mutationFn: (integrationId: string) =>
      apiPost<IntegrationTestResult>(
        `/api/v1/integrations/${integrationId}/test`,
        {},
      ),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      apiDelete<{ status: string }>(`/api/v1/integrations/${integrationId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useSyslogSources() {
  return useQuery<SyslogSourcesResponse>({
    queryKey: ["syslog-sources"],
    queryFn: () => apiGet<SyslogSourcesResponse>("/api/v1/syslog/sources"),
  });
}

export function useCreateSyslogSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSyslogSourceRequest) =>
      apiPost<{ source: SyslogSourceCreated }>("/api/v1/syslog/sources", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["syslog-sources"] });
    },
  });
}

export function useDeleteSyslogSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      apiDelete<{ status: string }>(`/api/v1/syslog/sources/${sourceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["syslog-sources"] });
    },
  });
}

export function usePatchIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ integrationId, enabled }: { integrationId: string; enabled: boolean }) =>
      apiPatch<{ integration: Integration }>(`/api/v1/integrations/${integrationId}`, {
        enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

// ── MSP ───────────────────────────────────────────────────

export function useMspTenants(params?: { limit?: number; offset?: number }) {
  return useQuery<MspTenantsResponse>({
    queryKey: ["msp", "tenants", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<MspTenantsResponse>("/api/v1/msp/tenants", qs);
    },
    refetchInterval: 60_000,
  });
}

export function useMspDashboardSummary(hours: number = 24) {
  return useQuery<MspDashboardSummaryResponse>({
    queryKey: ["msp", "dashboard", "summary", hours],
    queryFn: () =>
      apiGet<MspDashboardSummaryResponse>("/api/v1/msp/dashboard/summary", {
        hours: String(hours),
      }),
    refetchInterval: 30_000,
  });
}

export function useMspDashboardHealth() {
  return useQuery<MspDashboardHealthResponse>({
    queryKey: ["msp", "dashboard", "health"],
    queryFn: () =>
      apiGet<MspDashboardHealthResponse>("/api/v1/msp/dashboard/health"),
    refetchInterval: 60_000,
  });
}

export function useMspBilling(period: string) {
  return useQuery<MspBillingResponse>({
    queryKey: ["msp", "billing", period],
    queryFn: () =>
      apiGet<MspBillingResponse>("/api/v1/msp/billing/summary", {
        period,
        detail: "true",
      }),
  });
}

export function useMspTenantAlerts(
  tenantId: string | null,
  params?: { status?: string; severity?: string; limit?: number; offset?: number },
) {
  return useQuery<AlertsResponse>({
    queryKey: ["msp", "tenant-alerts", tenantId, params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.status) qs.status = params.status;
      if (params?.severity) qs.severity = params.severity;
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<AlertsResponse>("/api/v1/alerts", qs, {
        targetTenantId: tenantId!,
      });
    },
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });
}

// #775: MSP cross-tenant triage. Same target-tenant header pattern as
// useMspTenantAlerts, but mutating — ack/resolve a child's alerts from the
// drill-down. Bulk with a single id covers per-row actions too, so no separate
// single-alert hook is needed here. Invalidate the child's alert list + the MSP
// dashboards (fleet counts shift when a child alert changes status).
export function useMspBulkUpdateTenantAlerts(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertIds, status }: { alertIds: string[]; status: AlertStatus }) =>
      apiPost<{ updated: number }>(
        "/api/v1/alerts/bulk",
        { alert_ids: alertIds, status },
        { targetTenantId: tenantId! },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["msp", "tenant-alerts", tenantId] });
      qc.invalidateQueries({ queryKey: ["msp", "dashboard"] });
    },
  });
}

export function useMspCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MspCreateTenantRequest) =>
      apiPost<MspCreateTenantResponse>("/api/v1/msp/tenants", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["msp", "tenants"] });
      qc.invalidateQueries({ queryKey: ["msp", "dashboard", "summary"] });
      qc.invalidateQueries({ queryKey: ["msp", "dashboard", "health"] });
      qc.invalidateQueries({ queryKey: ["msp", "onboarding"] });
    },
  });
}

export function useMspBulkCreateTenants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clients: MspBulkCreateClient[]) =>
      apiPost<MspBulkCreateResponse>("/api/v1/msp/tenants/bulk", { clients }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["msp", "tenants"] });
      qc.invalidateQueries({ queryKey: ["msp", "dashboard", "summary"] });
      qc.invalidateQueries({ queryKey: ["msp", "dashboard", "health"] });
      qc.invalidateQueries({ queryKey: ["msp", "onboarding"] });
    },
  });
}

export function useMspOnboardingSummary() {
  return useQuery<MspOnboardingSummaryResponse>({
    queryKey: ["msp", "onboarding", "summary"],
    queryFn: () =>
      apiGet<MspOnboardingSummaryResponse>("/api/v1/msp/onboarding/summary"),
    refetchInterval: 300_000,
  });
}

// ── Compliance Reports ───────────────────────────────────────

export function useComplianceReports(limit = 20, offset = 0) {
  return useQuery<ComplianceReportsResponse>({
    queryKey: ["compliance", "reports", { limit, offset }],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (limit) qs.limit = String(limit);
      if (offset) qs.offset = String(offset);
      return apiGet<ComplianceReportsResponse>("/api/v1/reports/compliance", qs);
    },
  });
}

export function useComplianceReport(reportId: string | null) {
  return useQuery<ComplianceReportResponse>({
    queryKey: ["compliance", "report", reportId],
    queryFn: () =>
      apiGet<ComplianceReportResponse>(`/api/v1/reports/compliance/${reportId}`),
    enabled: !!reportId,
    refetchInterval: (query) => {
      const status = query.state.data?.report?.status;
      if (status === "pending" || status === "generating") return 5000;
      return false;
    },
  });
}

export function useCreateComplianceReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateComplianceReportRequest) =>
      apiPost<ComplianceReportResponse>("/api/v1/reports/compliance", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["compliance", "reports"] });
    },
  });
}

// On-demand MSP portfolio report (#783). Triggers async generation; poll the
// returned report id via useComplianceReport, download via the shared compliance
// GET (?download=true). The row is a compliance_reports row, so refreshing the
// compliance-reports list keeps the Reports-page history in sync.
export function useGenerateMspPortfolioReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { period_start: string; period_end: string }) =>
      apiPost<ComplianceReportResponse>("/api/v1/msp/reports/portfolio", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["compliance", "reports"] });
    },
  });
}

// ── Notifications ─────────────────────────────────────────

export function useNotificationConfigs() {
  return useQuery<NotificationConfigsResponse>({
    queryKey: ["notifications", "configs"],
    queryFn: () =>
      apiGet<NotificationConfigsResponse>("/api/v1/notifications/configs"),
  });
}

export function useCreateNotificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateNotificationConfigRequest) =>
      apiPost<{ config: NotificationConfig }>(
        "/api/v1/notifications/configs",
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useUpdateNotificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      ...data
    }: UpdateNotificationConfigRequest & { configId: string }) =>
      apiPatch<{ config: NotificationConfig }>(
        `/api/v1/notifications/configs/${configId}`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteNotificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) =>
      apiDelete<{ status: string; config_id: string }>(
        `/api/v1/notifications/configs/${configId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ── Notification policies (#665 P1 detect-but-mute) ──
export function useNotificationPolicies() {
  return useQuery<NotificationPoliciesResponse>({
    queryKey: ["notifications", "policies"],
    queryFn: () =>
      apiGet<NotificationPoliciesResponse>("/api/v1/notifications/policies"),
  });
}

export function useUpsertNotificationPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertNotificationPolicyRequest) =>
      apiPut<{ policy: NotificationPolicy }>(
        "/api/v1/notifications/policies",
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteNotificationPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      apiDelete<{ status: string; policy_id: string }>(
        `/api/v1/notifications/policies/${policyId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useTestNotificationConfig() {
  return useMutation({
    mutationFn: (configId: string) =>
      apiPost<TestNotificationResponse>(
        `/api/v1/notifications/configs/${configId}/test`,
        {},
      ),
  });
}

// ── Branding ──────────────────────────────────────────────

export function useBranding() {
  return useQuery<BrandingResponse>({
    queryKey: ["branding"],
    queryFn: () => apiGet<BrandingResponse>("/api/v1/branding"),
  });
}

export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<BrandingConfig>) =>
      apiPut<BrandingResponse>("/api/v1/branding", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["branding"] });
    },
  });
}

export function useLogoUploadUrl() {
  return useMutation({
    mutationFn: (data: { content_type: string; file_size: number }) =>
      apiPost<LogoUploadResponse>("/api/v1/branding/logo-upload-url", data),
  });
}

// ── Billing ───────────────────────────────────────────────

export function useBillingSubscription() {
  return useQuery<BillingSubscriptionResponse>({
    queryKey: ["billing", "subscription"],
    queryFn: () =>
      apiGet<BillingSubscriptionResponse>("/api/v1/billing/subscription"),
  });
}

export function useBillingUsage(months: number = 3) {
  return useQuery<BillingUsageResponse>({
    queryKey: ["billing", "usage", months],
    queryFn: () =>
      apiGet<BillingUsageResponse>("/api/v1/billing/usage", {
        months: String(months),
      }),
  });
}

export function useBillingUsageRealtime() {
  return useQuery<BillingUsageRealtimeResponse>({
    queryKey: ["billing", "usage", "realtime"],
    queryFn: () =>
      apiGet<BillingUsageRealtimeResponse>("/api/v1/billing/usage/realtime"),
    refetchInterval: 60_000,
  });
}

export function useBillingStatus(enabled: boolean = true) {
  return useQuery<BillingStatusResponse>({
    queryKey: ["billing", "status"],
    queryFn: () => apiGet<BillingStatusResponse>("/api/v1/billing/status"),
    enabled,
    // Poll while the checkout webhook is still in flight; stop once a live
    // subscription lands so a left-open Billing tab doesn't poll forever.
    refetchInterval: (query) => {
      const status = query.state.data?.subscription?.status;
      const settled = Boolean(status) && status !== "canceled" && status !== "expired";
      return settled || !query.state.data?.pending_checkout ? false : 5_000;
    },
  });
}

export function useBillingProfile() {
  return useQuery<BillingProfileResponse>({
    queryKey: ["billing", "profile"],
    queryFn: () => apiGet<BillingProfileResponse>("/api/v1/billing/profile"),
  });
}

export function useUpdateBillingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Omit<BillingProfile, "tenant_id" | "created_at" | "updated_at">,
    ) => apiPut<BillingProfileResponse>("/api/v1/billing/profile", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing", "profile"] }),
  });
}

export function useBillingInvoices() {
  return useQuery<BillingInvoicesResponse>({
    queryKey: ["billing", "invoices"],
    queryFn: () => apiGet<BillingInvoicesResponse>("/api/v1/billing/invoices"),
  });
}

export function useIssueBillingInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      apiPost<{ invoice: { id: string; document_number: string; status: string } }>(
        `/api/v1/billing/invoices/${invoiceId}/issue`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing", "invoices"] }),
  });
}

export function useDownloadBillingInvoice() {
  return useMutation({
    mutationFn: (invoiceId: string) =>
      apiGet<BillingInvoiceDownloadResponse>(
        `/api/v1/billing/invoices/${invoiceId}/download`,
      ),
  });
}

export function useCreateBillingCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { plan: string; return_url: string }) =>
      apiPost<BillingSubscribeResponse>("/api/v1/billing/subscribe", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

export function useCreateBillingPortal() {
  return useMutation({
    mutationFn: (data: { return_url: string }) =>
      apiPost<BillingPortalResponse>("/api/v1/billing/portal", data),
  });
}

export function useCancelBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ subscription: BillingSubscription; provider?: string }>(
        "/api/v1/billing/cancel",
        { at_period_end: true },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing"] }),
  });
}

// ── Users ─────────────────────────────────────────────────

export function useUsers() {
  return useQuery<UsersResponse>({
    queryKey: ["users"],
    queryFn: () => apiGet<UsersResponse>("/api/v1/users"),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) =>
      apiPost<{ user: TenantUser }>("/api/v1/users", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: UpdateUserRequest & { userId: string }) =>
      apiPatch<{ user: TenantUser }>(`/api/v1/users/${userId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiDelete<{ status: string; user_id: string }>(`/api/v1/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// ── Investigation ─────────────────────────────────────────

export function useStartSearch() {
  return useMutation({
    mutationFn: (data: SearchRequest) =>
      apiPost<SearchStartResponse>("/api/v1/investigation/search", data),
  });
}

export function useTranslateInvestigationQuery() {
  return useMutation({
    mutationFn: (data: NaturalLanguageSearchRequest) =>
      apiPost<NaturalLanguageTranslation>(
        "/api/v1/investigation/search/translate",
        data,
      ),
  });
}

export function useSearchStatus(executionId: string | null) {
  return useQuery<SearchResult>({
    queryKey: ["investigation", "search", executionId],
    queryFn: () =>
      apiGet<SearchResult>(
        `/api/v1/investigation/search/${executionId}`,
      ),
    enabled: !!executionId,
    refetchInterval: (query) =>
      isSearchInFlight(query.state.data?.status) ? 2000 : false,
  });
}

export function useStartEntityTimeline() {
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      days,
    }: {
      entityType: string;
      entityId: string;
      days?: number;
    }) =>
      apiPost<EntityTimelineStartResponse>(
        `/api/v1/investigation/entities/${entityType}/${entityId}/timeline${days ? `?days=${days}` : ""}`,
        {},
      ),
  });
}

export function useEntityTimeline(
  executionId: string | null,
  entityId: string,
  entityType: string,
) {
  return useQuery<EntityTimelineResult>({
    queryKey: ["investigation", "entity-timeline", executionId, entityId, entityType],
    queryFn: () =>
      apiGet<EntityTimelineResult>(
        `/api/v1/investigation/entities/timeline/${executionId}`,
        // entity_type drives per-type validation server-side — without it the
        // route defaults to "ip" and 400s user/host ids.
        { entity_id: entityId, entity_type: entityType },
      ),
    enabled: !!executionId,
    refetchInterval: (query) =>
      isSearchInFlight(query.state.data?.status) ? 2000 : false,
  });
}

export function useSavedSearches() {
  return useQuery<SavedSearchesResponse>({
    queryKey: ["investigation", "saved-searches"],
    queryFn: () =>
      apiGet<SavedSearchesResponse>("/api/v1/investigation/searches/saved"),
  });
}

export function useCreateSavedSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; query_json: Record<string, unknown> }) =>
      apiPost<{ saved_search: SavedSearch }>(
        "/api/v1/investigation/searches/saved",
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investigation", "saved-searches"] });
    },
  });
}

// ── Onboarding ───────────────────────────────────────────

export function useOnboardingChecklist(tenantId: string | undefined) {
  return useQuery<OnboardingChecklist>({
    queryKey: ["onboarding", "checklist", tenantId],
    queryFn: () =>
      apiGet<OnboardingChecklist>(
        `/api/v1/onboarding/checklist/${tenantId}`,
      ),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

// ── Agent Keys ───────────────────────────────────────────

export function useAgentKeys() {
  return useQuery<AgentKeysResponse>({
    queryKey: ["agent", "keys"],
    queryFn: () => apiGet<AgentKeysResponse>("/api/v1/agent/keys"),
  });
}

export function useCreateAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentKeyRequest) =>
      apiPost<CreateAgentKeyResponse>("/api/v1/agent/keys", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", "keys"] });
      qc.invalidateQueries({ queryKey: ["onboarding"] });
    },
  });
}

export function useRevokeAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiDelete<{ status: string; key_id: string }>(
        `/api/v1/agent/keys/${keyId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", "keys"] });
    },
  });
}

// ── Fleet Keys (#180-C) ──────────────────────────────────

const FLEET_KEYS_QUERY_KEY = ["agent", "fleet-keys"] as const;

export function useFleetKeys() {
  return useQuery<FleetKeysResponse>({
    queryKey: FLEET_KEYS_QUERY_KEY,
    queryFn: () => apiGet<FleetKeysResponse>("/api/v1/agent/fleet-keys"),
  });
}

export function useCreateFleetKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFleetKeyRequest) =>
      apiPost<FleetKeyMintResponse>("/api/v1/agent/fleet-keys", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FLEET_KEYS_QUERY_KEY });
    },
  });
}

export function useRevokeFleetKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiDelete<{ status: string; fleet_key_id: string }>(
        `/api/v1/agent/fleet-keys/${keyId}`,
      ),
    // Optimistic: drop the row immediately so the UI reflects the revoke
    // without waiting for the round-trip, then reconcile against the server on
    // settle (re-fetch). Roll back to the snapshot if the DELETE fails.
    onMutate: async (keyId) => {
      await qc.cancelQueries({ queryKey: FLEET_KEYS_QUERY_KEY });
      const prev = qc.getQueryData<FleetKeysResponse>(FLEET_KEYS_QUERY_KEY);
      qc.setQueryData<FleetKeysResponse>(FLEET_KEYS_QUERY_KEY, (old) =>
        old ? { fleet_keys: old.fleet_keys.filter((k) => k.id !== keyId) } : old,
      );
      return { prev };
    },
    onError: (_err, _keyId, ctx) => {
      if (ctx?.prev) qc.setQueryData(FLEET_KEYS_QUERY_KEY, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: FLEET_KEYS_QUERY_KEY });
    },
  });
}

// Hosts deployed under one fleet key. The caller mounts this only on an
// expanded row, so the query fires at expand time and refetches on re-expand.
// staleTime:0 overrides the global 30s default — without it, a collapse +
// re-expand within 30s would be served from cache and the host list wouldn't
// refresh, contradicting the Phase-1 "collapse and re-expand to refresh"
// behavior (no polling).
export function useFleetKeyHosts(keyId: string) {
  return useQuery<DeployAgentsResponse>({
    queryKey: ["deploy", "agents", "fleet", keyId],
    queryFn: () =>
      apiGet<DeployAgentsResponse>("/api/v1/deploy/agents", {
        fleet_key_id: keyId,
      }),
    staleTime: 0,
  });
}

// ── Deploy ───────────────────────────────────────────────

export function useDeployAgents(options?: { enabled?: boolean }) {
  return useQuery<DeployAgentsResponse>({
    queryKey: ["deploy", "agents"],
    queryFn: () => apiGet<DeployAgentsResponse>("/api/v1/deploy/agents"),
    enabled: options?.enabled ?? true,
    // Poll while the tab is open (global refetchOnWindowFocus is off) so the
    // agent-health view shows fresh heartbeat data and the render-time
    // staleness check (#625, 15-min threshold) actually re-evaluates rather
    // than freezing at the last fetch on a long-open Settings tab.
    refetchInterval: 60_000,
  });
}

// ── Vulnerabilities ──────────────────────────────────────

export function useVulnerabilities(params?: {
  severity?: string;
  status?: string;
  asset_id?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery<VulnerabilitiesResponse>({
    queryKey: ["vulnerabilities", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.severity) qs.severity = params.severity;
      if (params?.status) qs.status = params.status;
      if (params?.asset_id) qs.asset_id = params.asset_id;
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<VulnerabilitiesResponse>("/api/v1/vulnerabilities", qs);
    },
  });
}

// ── CSPM (Cloud Posture) ─────────────────────────────────

export function useCspmSummary() {
  return useQuery<CspmPostureSummary>({
    queryKey: ["cspm", "summary"],
    queryFn: () => apiGet<CspmPostureSummary>("/api/v1/cspm/summary"),
    refetchInterval: 60_000,
  });
}

export function useCspmFindings(params?: {
  status?: string;
  severity?: string;
  cloud_provider?: string;
  check_id?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery<CspmFindingsResponse>({
    queryKey: ["cspm", "findings", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.status) qs.status = params.status;
      if (params?.severity) qs.severity = params.severity;
      if (params?.cloud_provider) qs.cloud_provider = params.cloud_provider;
      if (params?.check_id) qs.check_id = params.check_id;
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<CspmFindingsResponse>("/api/v1/cspm/findings", qs);
    },
    refetchInterval: 60_000,
  });
}

export function useVulnerabilitySummary() {
  return useQuery<VulnerabilitySummaryResponse>({
    queryKey: ["vulnerabilities", "summary"],
    queryFn: () =>
      apiGet<VulnerabilitySummaryResponse>("/api/v1/vulnerabilities/summary"),
    refetchInterval: 60_000,
  });
}

export function useUpdateVulnerability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vulnId, status }: { vulnId: string; status: string }) =>
      apiPatch<{ vulnerability: Vulnerability }>(
        `/api/v1/vulnerabilities/${vulnId}`,
        { status },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
    },
  });
}

export function useCspmFinding(findingId: string | null) {
  return useQuery<{ finding: CspmFinding }>({
    queryKey: ["cspm", "finding", findingId],
    queryFn: () => apiGet<{ finding: CspmFinding }>(`/api/v1/cspm/findings/${findingId}`),
    enabled: !!findingId,
  });
}

export function useCspmChecks() {
  return useQuery<CspmChecksResponse>({
    queryKey: ["cspm", "checks"],
    queryFn: () => apiGet<CspmChecksResponse>("/api/v1/cspm/checks"),
    staleTime: 300_000,
  });
}

export function useCspmScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<CspmScanResult>("/api/v1/cspm/scan", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cspm"] });
    },
  });
}

// ── Threat Intel ──────────────────────────────────────────────

export function useThreatIntelSummary() {
  return useQuery<ThreatIntelSummary>({
    queryKey: ["threat-intel", "summary"],
    queryFn: () => apiGet<ThreatIntelSummary>("/api/v1/threat-intel/summary"),
    refetchInterval: 60_000,
  });
}

export function useThreatIntelSearch(query: string) {
  return useQuery<ThreatIntelSearchResponse>({
    queryKey: ["threat-intel", "search", query],
    queryFn: () =>
      apiGet<ThreatIntelSearchResponse>(
        `/api/v1/threat-intel/search?q=${encodeURIComponent(query)}`
      ),
    enabled: query.length >= 3,
  });
}

export function useThreatIntelFeeds() {
  return useQuery<ThreatIntelFeedsResponse>({
    queryKey: ["threat-intel", "feeds"],
    queryFn: () => apiGet<ThreatIntelFeedsResponse>("/api/v1/threat-intel/feeds"),
    staleTime: 60_000,
  });
}

// ── Executive Dashboard ──────────────────────────────────

export function useExecutiveSummary() {
  return useQuery<ExecutiveSummaryResponse>({
    queryKey: ["executive", "summary"],
    queryFn: () =>
      apiGet<ExecutiveSummaryResponse>("/api/v1/executive/summary"),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useExecutiveMetrics(days: number = 7) {
  return useQuery<ExecutiveMetricsResponse>({
    queryKey: ["executive", "metrics", days],
    queryFn: () =>
      apiGet<ExecutiveMetricsResponse>("/api/v1/executive/metrics", {
        days: String(days),
      }),
    staleTime: 5 * 60_000,
  });
}

// ── Report Schedules ──────────────────────────────────────

export function useReportSchedules() {
  return useQuery<ReportSchedulesResponse>({
    queryKey: ["reports", "schedules"],
    queryFn: () => apiGet<ReportSchedulesResponse>("/api/v1/reports/schedules"),
  });
}

export function useCreateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateReportScheduleRequest) =>
      apiPost<{ schedule: ReportSchedule }>("/api/v1/reports/schedules", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "schedules"] });
    },
  });
}

export function useSuppressFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) =>
      apiPost<{ finding: CspmFinding }>(`/api/v1/cspm/findings/${findingId}/suppress`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cspm"] });
    },
  });
}

export function useUpdateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      ...data
    }: UpdateReportScheduleRequest & { scheduleId: string }) =>
      apiPatch<{ schedule: ReportSchedule }>(
        `/api/v1/reports/schedules/${scheduleId}`,
        data
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "schedules"] });
    },
  });
}

export function useDeleteReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      apiDelete<{ status: string }>(
        `/api/v1/reports/schedules/${scheduleId}`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "schedules"] });
    },
  });
}

export function usePreviewReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      apiPost<ComplianceReportResponse>(
        `/api/v1/reports/schedules/${scheduleId}/preview`,
        {}
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", "history"] });
    },
  });
}

export function useReportHistory(type?: string, limit = 20, offset = 0) {
  return useQuery<ReportHistoryResponse>({
    queryKey: ["reports", "history", { type, limit, offset }],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (type) qs.type = type;
      if (limit) qs.limit = String(limit);
      if (offset) qs.offset = String(offset);
      return apiGet<ReportHistoryResponse>("/api/v1/reports/history", qs);
    },
  });
}

export function useUnsuppressFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) =>
      apiPost<{ finding: CspmFinding }>(`/api/v1/cspm/findings/${findingId}/unsuppress`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cspm"] });
    },
  });
}

export function useExecutiveExport() {
  return useMutation({
    mutationFn: () =>
      apiPost<ExecutiveExportResponse>("/api/v1/executive/export", {}),
  });
}

// ── Billing / Lifecycle State ────────────────────────────

/**
 * Trial / grace / closed lifecycle state for the current tenant.
 *
 * Powers `<LifecycleBanner>` (#228). The endpoint sends
 * `Cache-Control: private, max-age=30`, and we layer a 45s React Query
 * staleTime plus refetch on window-focus so a transition surfaces
 * within ~a minute without hammering the API.
 */
export function useBillingState() {
  return useQuery<BillingState>({
    queryKey: ["me", "billing-state"],
    queryFn: () => apiGet<BillingState>("/api/v1/me/billing-state"),
    staleTime: 45_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Per-device freshness for the calling tenant (#289 / #270).
 *
 * Shared by `/app/hosts` and the AppShell freshness chip. The single
 * queryKey lets React Query coalesce the two consumers into one
 * network request; both refresh together when revoke invalidates.
 * Backend sends `Cache-Control: private, max-age=15` so a 60s poll
 * is well above the cache TTL without hammering.
 */
export function useHosts() {
  return useQuery<HostsResponse>({
    queryKey: ["me", "hosts"],
    queryFn: () => apiGet<HostsResponse>("/api/v1/me/hosts"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Effective-tenant identity used by the production application shell. */
export function useWorkspace() {
  return useQuery<Workspace>({
    queryKey: ["me", "workspace"],
    queryFn: () => apiGet<Workspace>("/api/v1/me/workspace"),
    staleTime: 5 * 60_000,
  });
}
